import {
  ContentLayout,
  CustomeDialog,
  Loader,
  LoadingButton,
  ServerPaginatedTable,
} from "../../../shared";
import * as React from "react";
import {
  Stack,
  Typography,
  Button,
  Tooltip,
  Link as ButtonLink,
} from "@mui/material";
import { sortItems, errorMessage, successMessage } from "../../../utils";
import SimpleReactValidator from "simple-react-validator";
import InputControl from "../../../shared/components/InputControl";
import { Link } from "react-router-dom";
import { useTandaList } from "../hooks/useTandaList";
import ConnectTandaDialog from "../components/ConnectTandaDialog";
import { SelectDataStreamDialog } from "../components/SelectDataStreamDialog";

export const TandaList = () => {
  const [, forceUpdate] = React.useState();
  const validator = React.useRef(
    new SimpleReactValidator({ autoForceUpdate: { forceUpdate: forceUpdate } })
  );
  const {
    state,
    isEditable,
    redirectURL,
    handleTableSearch,
    gotoPage,
    setPageSize,
    handleSortBy,
    handleModal,
    handleStoreConnect,
    handleStoreDisconnect, // Add this import
    isErrorModal,
    onChange,
    handleStatusFilter,
    resetFilters,
    onChangeDataStream,
    handleDataStreamSelect,
  } = useTandaList({
    isList: true,
  });

  const { selectedStore, tandaList, isLoading, search, pagination, sortBy } =
    state;

  const ActionButtons = ({ value, id }) => {
    return (
      <div className="action-buttons">
        {value?.last_sync && (
          <Stack gap="6px">
            <Typography variant="caption" color="#808080">
              Last update was on {value?.last_sync} {value?.last_sync_status}
            </Typography>
          </Stack>
        )}
        {isEditable && (
          <>
            {value?.status ? (
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => handleModal("disconnect", true, value)}
              >
                Disconnect
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleModal("connect", true, value)}
              >
                Connect
              </Button>
            )}
          </>
        )}
        <Link to={`details/${id}`}>
          <img alt="details" src="/icons/arrow_forward.svg" />
        </Link>
      </div>
    );
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Store",
        accessor: "store",
        sortType: (prev, curr, columnId) => {
          return sortItems(prev, curr, columnId);
        },
        Cell: ({ value, row }) => {
          let app_id = row.original.application_id;
          return (
            <Stack direction="row" gap={2}>
              <Typography>{value}</Typography>
              {app_id && (
                <Tooltip title={app_id} placement="top" arrow>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 400,
                      bgcolor: "#0000000F",
                      p: "4px 7px",
                      borderRadius: "8px",
                      maxWidth: "160px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {app_id}
                  </Typography>
                </Tooltip>
              )}
            </Stack>
          );
        },
      },

      {
        Header: "Action",
        id: "id",
        accessor: "id",
        align: "right",
        disableSortBy: true,
        Cell: ({ row }) => {
          return <ActionButtons value={row.original} id={row.original.id} />;
        },
      },
    ],
    [isEditable]
  );

  const statusOption = [
    { value: "1", label: "Connected" },
    { value: "0", label: "Disconnected" },
  ];

  return (
    <>
      <ContentLayout title="Tanda">
        <ServerPaginatedTable
          columns={columns}
          data={tandaList}
          isLoading={isLoading}
          searchBar={true}
          defaultSearch={false}
          gotoPage={gotoPage}
          setPageSize={setPageSize}
          handleSort={handleSortBy}
          pagination={pagination}
          sortByGlobal={sortBy}
          filterContent={
            <>
              <InputControl
                type="table-search"
                name="search"
                placeholder="Search store..."
                onChange={handleTableSearch}
                value={search}
                onClear={() => handleTableSearch({ target: { value: "" } })}
              />
              <InputControl
                type="select"
                name="status"
                label="Connection Status"
                optionValue={statusOption}
                width="260px"
                onChange={(e) => handleStatusFilter(e)}
                value={state.filterApplied?.status?.value || ""}
              />
              <Button
                type="button"
                size="medium"
                variant="text"
                color="gray"
                sx={{
                  minWidth: "40px",
                  p: "8px",
                }}
                onClick={() => resetFilters()}
              >
                <img alt="close" src="/icons/ic_close.svg" />
              </Button>
            </>
          }
        />
      </ContentLayout>

      <CustomeDialog
        open={state?.isOpen}
        handleClose={() => handleModal("connect", false, null)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: { width: "423px", p: "16px" },
        }}
        title="Connect to Tanda"
        content={
          <ConnectTandaDialog
            onChange={onChange}
            state={state}
            handleModal={handleModal}
            handleUpdate={() =>
              handleStoreConnect(
                selectedStore,
                selectedStore?.status ? false : true
              )
            }
            redirectURL={redirectURL}
          />
        }
      />

      {/* Data Stream Selection Modal */}
      <CustomeDialog
        open={state.isDataStreamOpen}
        handleClose={() => handleModal("dataStream", false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: { width: "520px", p: "16px" },
        }}
        title="Select Data Stream"
        content={
          <SelectDataStreamDialog
            state={state}
            onChangeDataStream={onChangeDataStream}
            handleModal={handleModal}
            handleUpdate={handleDataStreamSelect}
          />
        }
      />

      <CustomeDialog
        open={state.isOpenConnect}
        handleClose={() => handleModal("disconnect", false, null)}
        PaperProps={{
          sx: { width: "423px", height: "155px", p: "16px" },
        }}
        title="Disconnect Tanda"
        content={
          <Stack
            mt="8px"
            gap="10px"
            height="100%"
            justifyContent="space-between"
          >
            <Typography variant="subtitle2">
              Are you sure you want to disconnect Tanda for{" "}
              {selectedStore?.store_name}?
            </Typography>
            <Stack direction="row" justifyContent="end" gap={2}>
              <Button
                variant="text"
                onClick={() => handleModal("disconnect", false, null)}
              >
                Cancel
              </Button>
              <LoadingButton
                loading={state.isSaveButton}
                variant="contained"
                color="error"
                onClick={() => handleStoreDisconnect(selectedStore)}
              >
                Disconnect
              </LoadingButton>
            </Stack>
          </Stack>
        }
      />
    </>
  );
};
